// Content page (added 2026-05-31). Portfolio-wide blog + content view.
// Three read-only panels:
//   1. Recent posts (last 30 days)        — by date_published DESC
//   2. Top posts by GSC impressions (30d) — joins gsc_query_page_daily
//   3. Recent content events (last 7d)    — content-changing agent_actions
//
// View-only — no edit/regen buttons. Edit/regen actions live on per-site
// pages. Sorts are server-defined; client just renders the tables.

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { contentApi } from "../lib/api";

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 16).replace("T", " ") + " UTC";
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function outcomeBadge(outcome: string): string {
  if (outcome === "ok") return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (outcome === "failed") return "bg-rose-50 text-rose-700 border-rose-200";
  if (outcome === "pending_approval") return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-ink-50 text-ink-700 border-ink-200";
}

export function ContentPage() {
  const recentQ = useQuery({ queryKey: ["content", "recent-posts"], queryFn: () => contentApi.recentPosts(30, 50) });
  const topQ = useQuery({ queryKey: ["content", "top-posts"], queryFn: () => contentApi.topPosts(30, 30) });
  const eventsQ = useQuery({ queryKey: ["content", "events"], queryFn: () => contentApi.events(7, 100) });

  return (
    <div>
      <div className="flex items-baseline justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold">Content</h1>
          <p className="text-ink-600 text-sm mt-1">
            Portfolio-wide blog + content view. New posts, top performers by GSC impressions, and recent content-changing agent activity.
          </p>
        </div>
      </div>

      {/* ─── Section 1: Recent posts (last 30 days) ───────────────── */}
      <section className="bg-white rounded-xl border border-ink-100 mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-100 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Recent posts <span className="text-ink-500 text-sm font-normal">— last 30 days</span></h2>
          <span className="text-xs text-ink-500">{recentQ.data?.posts.length ?? 0} posts</span>
        </div>
        {recentQ.isLoading && <div className="p-4 text-sm text-ink-500">Loading…</div>}
        {recentQ.isError && <div className="p-4 text-sm text-rose-600">Failed to load</div>}
        {recentQ.data && recentQ.data.posts.length === 0 && (
          <div className="p-4 text-sm text-ink-500">No posts created in the last 30 days.</div>
        )}
        {recentQ.data && recentQ.data.posts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase text-ink-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">Date</th>
                  <th className="text-left px-4 py-2 font-medium">Site</th>
                  <th className="text-left px-4 py-2 font-medium">Title</th>
                  <th className="text-left px-4 py-2 font-medium">Keyword</th>
                  <th className="text-left px-4 py-2 font-medium">Live</th>
                </tr>
              </thead>
              <tbody>
                {recentQ.data.posts.map((p) => (
                  <tr key={p.id} className="border-t border-ink-100">
                    <td className="px-4 py-2 text-ink-600 whitespace-nowrap">{fmtDate(p.datePublished ?? p.createdAt)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <Link to={`/sites/${p.siteId}`} className="text-blue-600 hover:underline">{p.domain}</Link>
                      {p.niche && <span className="text-ink-400 ml-2 text-xs">{p.niche}</span>}
                    </td>
                    <td className="px-4 py-2">{p.title}</td>
                    <td className="px-4 py-2 text-ink-600 text-xs">{p.keyword ?? "—"}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <a href={p.liveUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View ↗</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ─── Section 2: Top posts by GSC impressions (last 30 days) ─ */}
      <section className="bg-white rounded-xl border border-ink-100 mb-6 overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-100 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Top posts by GSC impressions <span className="text-ink-500 text-sm font-normal">— last 30 days</span></h2>
          <span className="text-xs text-ink-500">{topQ.data?.posts.length ?? 0} posts</span>
        </div>
        {topQ.isLoading && <div className="p-4 text-sm text-ink-500">Loading…</div>}
        {topQ.isError && <div className="p-4 text-sm text-rose-600">Failed to load</div>}
        {topQ.data && topQ.data.posts.length === 0 && (
          <div className="p-4 text-sm text-ink-500">
            No GSC impressions on any blog post in the last 30 days. New posts typically pick up impressions 5-10 days after publish.
          </div>
        )}
        {topQ.data && topQ.data.posts.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase text-ink-600">
                <tr>
                  <th className="text-right px-4 py-2 font-medium">Imps</th>
                  <th className="text-right px-4 py-2 font-medium">Clicks</th>
                  <th className="text-left px-4 py-2 font-medium">Site</th>
                  <th className="text-left px-4 py-2 font-medium">Title</th>
                  <th className="text-left px-4 py-2 font-medium">Top query</th>
                  <th className="text-left px-4 py-2 font-medium">Live</th>
                </tr>
              </thead>
              <tbody>
                {topQ.data.posts.map((p) => (
                  <tr key={p.id} className="border-t border-ink-100">
                    <td className="px-4 py-2 text-right font-mono">{p.imps30d.toLocaleString()}</td>
                    <td className="px-4 py-2 text-right font-mono">{p.clicks30d > 0 ? p.clicks30d.toLocaleString() : <span className="text-ink-400">0</span>}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <Link to={`/sites/${p.siteId}`} className="text-blue-600 hover:underline">{p.domain}</Link>
                    </td>
                    <td className="px-4 py-2">{p.title}</td>
                    <td className="px-4 py-2 text-ink-600 text-xs">{p.topQuery ?? "—"}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <a href={p.liveUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-xs">View ↗</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ─── Section 3: Recent content events (last 7 days) ────────── */}
      <section className="bg-white rounded-xl border border-ink-100 overflow-hidden">
        <div className="px-4 py-3 border-b border-ink-100 flex items-baseline justify-between">
          <h2 className="text-lg font-semibold">Recent content events <span className="text-ink-500 text-sm font-normal">— last 7 days</span></h2>
          <span className="text-xs text-ink-500">{eventsQ.data?.events.length ?? 0} events</span>
        </div>
        {eventsQ.isLoading && <div className="p-4 text-sm text-ink-500">Loading…</div>}
        {eventsQ.isError && <div className="p-4 text-sm text-rose-600">Failed to load</div>}
        {eventsQ.data && eventsQ.data.events.length === 0 && (
          <div className="p-4 text-sm text-ink-500">No content-changing events in the last 7 days.</div>
        )}
        {eventsQ.data && eventsQ.data.events.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase text-ink-600">
                <tr>
                  <th className="text-left px-4 py-2 font-medium">When</th>
                  <th className="text-left px-4 py-2 font-medium">Agent</th>
                  <th className="text-left px-4 py-2 font-medium">Action</th>
                  <th className="text-left px-4 py-2 font-medium">Site</th>
                  <th className="text-left px-4 py-2 font-medium">Outcome</th>
                </tr>
              </thead>
              <tbody>
                {eventsQ.data.events.map((e) => (
                  <tr key={e.id} className="border-t border-ink-100">
                    <td className="px-4 py-2 text-ink-600 whitespace-nowrap" title={fmtDateTime(e.startedAt)}>{relativeTime(e.startedAt)}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {e.agentEmoji && <span className="mr-1">{e.agentEmoji}</span>}
                      <span className="text-ink-700">{e.agentDisplayName}</span>
                    </td>
                    <td className="px-4 py-2 text-ink-600 text-xs"><code className="bg-ink-50 px-1 rounded">{e.actionKind}</code></td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {e.siteId
                        ? <Link to={`/sites/${e.siteId}`} className="text-blue-600 hover:underline">{e.domain ?? e.siteId.slice(0, 8)}</Link>
                        : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded border ${outcomeBadge(e.outcome)}`}>{e.outcome}</span>
                      {e.errorMessage && <span className="text-rose-600 text-xs ml-2" title={e.errorMessage}>⚠</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
